import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../../styles/components/landing/HowItWorks.css';

// Import icons from assets
import userIcon from '../../../assets/icons/landing/journal_icon.png';
import journalIcon from '../../../assets/icons/landing/clipBoard_icon.png';
import phoneIcon from '../../../assets/icons/landing/magnifiying_icon.png';
import secureIcon from '../../../assets/icons/landing/telephone_icon.png';
import startJournal from '../../../assets/icons/landing/book2_icon.png';
import progressIcon from '../../../assets/icons/landing/chart_icon.png';

// Professional icons
import verifyIcon from '../../../assets/icons/landing/journal_icon.png';
import chatIcon from '../../../assets/icons/landing/professional_icon.png';
import trackIcon from '../../../assets/icons/landing/manage2_icon.png';
import analyticsIcon from '../../../assets/icons/landing/chart2_icon.png';
import careIcon from '../../../assets/icons/landing/provideCare_icon.png';
import privacyIcon from '../../../assets/icons/landing/lock_icon.png';

const HowItWorks = () => {
  const navigate = useNavigate();
  
  // Patient carousel states
  const [patientCurrentSlide, setPatientCurrentSlide] = useState(0);
  const [patientIsDragging, setPatientIsDragging] = useState(false);
  const [patientStartPos, setPatientStartPos] = useState(0);
  const [patientCurrentTranslate, setPatientCurrentTranslate] = useState(0);
  const [patientPrevTranslate, setPatientPrevTranslate] = useState(0);
  
  // Professional carousel states
  const [professionalCurrentSlide, setProfessionalCurrentSlide] = useState(0);
  const [professionalIsDragging, setProfessionalIsDragging] = useState(false);
  const [professionalStartPos, setProfessionalStartPos] = useState(0);
  const [professionalCurrentTranslate, setProfessionalCurrentTranslate] = useState(0);
  const [professionalPrevTranslate, setProfessionalPrevTranslate] = useState(0);
  
  const patientSliderRef = useRef(null);
  const professionalSliderRef = useRef(null);

  // Patient journey data
  const patientSteps = [
    {
      id: 1,
      title: "1. Create Your Neurolex Account",
      subtitle: "Start your mental wellness journey by signing up on our mobile or web platform. Create your personal profile by entering essential details and setting emergency contacts to ensure your safety and support.",
      description: "Start your mental wellness journey by signing up on our mobile or web platform. Create your personal profile by entering essential details and setting emergency contacts to ensure your safety and support.",
      icon: userIcon,
      color: "#4CAF50"
    },
    {
      id: 2,
      title: "2. Complete the Onboarding",
      subtitle: "Answer a series of questions designed to better understand your mental health background, daily habits, emotional patterns, and current concerns. This step helps Neurolex create a foundation for personalized care and informed support.",
      description: "Answer a series of questions designed to better understand your mental health background, daily challenges, and specific wellness concerns. This step helps Neurolex create a foundation for personalized care and informed support.",
      icon: journalIcon,
      color: "#FF9800"
    },
    {
      id: 3,
      title: "3. Choose a Professional",
      subtitle: "Neurolex lets you browse licensed, verified mental health professionals available at your clinic. Review their credentials, explore their therapeutic styles, and schedule your first appointment directly in the app.",
      description: "Neurolex lets you browse licensed, verified mental health professionals available at your clinic. Review their profiles, specializations, and therapeutic styles, and schedule your first appointment directly in the app.",
      icon: phoneIcon,
      color: "#2196F3"
    },
    {
      id: 4,
      title: "4. Begin Journaling",
      subtitle: "fter matching, you'll unlock your private journal to regularly log thoughts, emotions, and experiences. Our AI gently analyzes patterns and emotional cues to help your provider track emotional patterns and better support your care.",
      description: "After matching, you'll unlock your private journal to regularly log thoughts, emotions, and experiences. Our AI gently analyzes patterns and emotional cues to help your provider track emotional patterns and better support your care.",
      icon: startJournal,
      color: "#9C27B0"
    },
    {
      id: 5,
      title: "5. Attend Secure Appointments",
      subtitle: "Connect with your provider through encrypted video or voice sessions, schedule directly within the platform. Access convenient in-app messaging, appointment reminders, and payment so you can focus entirely on your session.",
      description: "Connect with your provider through encrypted video or voice sessions, schedule directly within the platform. Access convenient in-app messaging, appointment reminders, and payment so you can focus entirely on your session.",
      icon: secureIcon,
      color: "#FF5722"
    },
    {
      id: 6,
      title: "6. Monitor Your Growth Over Time",
      subtitle: "As you continue journaling and attending sessions, your provider gains deeper visibility into your progress and emotional patterns. This data-driven approach practically and ensures your treatment evolves with your needs.",
      description: "As you continue journaling and attending sessions, your provider gains deeper visibility into your progress and emotional patterns. This data-driven approach practically and ensures your treatment evolves with your needs.",
      icon: progressIcon,
      color: "#607D8B"
    }
  ];

  // Professional journey data
  const professionalSteps = [
    {
      id: 1,
      title: "1. Apply & Get Verified",
      subtitle: "Submit your credentials, licenses, and background for review. Our team verifies your qualifications to ensure a trusted network of providers. Once approved, you can join Neurolex and start connecting with patients.",
      description: "Submit your credentials, licenses, and background for review. Our team verifies your qualifications to ensure a trusted network of providers. Once verified, you'll gain access to our professional dashboard.",
      icon: verifyIcon,
      color: "#4CAF50"
    },
    {
      id: 2,
      title: "2. Set Up Your Professional Profile",
      subtitle: "Complete your profile by adding your specialties, clinical experience, certifications, and availability. Providing this detailed information helps patients find the right match and better understand your unique approach to care.",
      description: "Complete your profile by adding your specializations, clinical experience, certifications, and availability. Providing this detailed information helps patients understand your unique approach to care.",
      icon: chatIcon,
      color: "#FF9800"
    },
    {
      id: 3,
      title: "3. Manage Patient Profiles",
      subtitle: "Access detailed patient information including onboarding data and session history. Review AI-generated insights derived from patients’ journal entries to better understand emotional patterns and tailor your care.",
      description: "Access patient information and review AI-generated insights derived from patient's journal entries and behavioral data. Review AI-generated insights derived from patients' journal entries and behavioral data.",
      icon: trackIcon,
      color: "#2196F3"
    },
    {
      id: 4,
      title: "4. Provide Care",
      subtitle: "Conduct secure, HIPAA-compliant video or voice sessions with your patients. Schedule and manage appointments efficiently, and maintain seamless, confidential communication through the platform to support ongoing care.",
      description: "Conduct secure video or voice sessions through our platform, with access to calendar integration and session notes. Use comprehensive patient insights to provide more informed, effective support ongoing care.",
      icon: careIcon,
      color: "#9C27B0"
    },
    {
      id: 5,
      title: "5. Track Progress & Outcomes",
      subtitle: "Regularly monitor patient progress over time using detailed, data-driven reports and AI-generated insights. Use this valuable information to refine treatment plans, identify emerging trends, and enhance overall patient outcomes effectively.",
      description: "Evaluate treatment progress over time using detailed, easy-to-read reports and AI-generated insights. Use this valuable information to refine treatment plans and enhance overall patient outcomes effectively.",
      icon: analyticsIcon,
      color: "#FF5722"
    },
    {
      id: 6,
      title: "6. Maintain Privacy & Compliance",
      subtitle: "Ensure all patient data and communications remain confidential and compliant with HIPAA and other regulations. Neurolex’s secure, encrypted platform provides strong protection to safeguard your practice and patients’ sensitive information.",
      description: "Ensure all patient data and communications remain confidential and compliant with HIPAA and other regulations. Neurolex's secure, encrypted platform maintains the highest standards for protecting sensitive information.",
      icon: privacyIcon,
      color: "#607D8B"
    }
  ];

  // Carousel utility functions
  const getPositionX = (event) => {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  };

  // Patient Carousel Functions
  const nextPatientSlide = () => {
    setPatientCurrentSlide(prev => (prev + 1) % patientSteps.length);
  };

  const prevPatientSlide = () => {
    setPatientCurrentSlide(prev => (prev - 1 + patientSteps.length) % patientSteps.length);
  };

  const goToPatientSlide = (index) => {
    setPatientCurrentSlide(index);
  };

  const handlePatientTouchStart = (event) => {
    setPatientIsDragging(true);
    const posX = getPositionX(event);
    setPatientStartPos(posX);
    setPatientCurrentTranslate(patientPrevTranslate);
  };

  const handlePatientTouchMove = (event) => {
    if (!patientIsDragging) return;
    const currentPosition = getPositionX(event);
    setPatientCurrentTranslate(patientPrevTranslate + currentPosition - patientStartPos);
  };

  const handlePatientTouchEnd = () => {
    if (!patientIsDragging) return;
    setPatientIsDragging(false);
    
    const movedBy = patientCurrentTranslate - patientPrevTranslate;
    const threshold = 100;
    
    if (movedBy < -threshold && patientCurrentSlide < patientSteps.length - 1) {
      nextPatientSlide();
    } else if (movedBy > threshold && patientCurrentSlide > 0) {
      prevPatientSlide();
    }
  };

  // Professional Carousel Functions
  const nextProfessionalSlide = () => {
    setProfessionalCurrentSlide(prev => (prev + 1) % professionalSteps.length);
  };

  const prevProfessionalSlide = () => {
    setProfessionalCurrentSlide(prev => (prev - 1 + professionalSteps.length) % professionalSteps.length);
  };

  const goToProfessionalSlide = (index) => {
    setProfessionalCurrentSlide(index);
  };

  const handleProfessionalTouchStart = (event) => {
    setProfessionalIsDragging(true);
    const posX = getPositionX(event);
    setProfessionalStartPos(posX);
    setProfessionalCurrentTranslate(professionalPrevTranslate);
  };

  const handleProfessionalTouchMove = (event) => {
    if (!professionalIsDragging) return;
    const currentPosition = getPositionX(event);
    setProfessionalCurrentTranslate(professionalPrevTranslate + currentPosition - professionalStartPos);
  };

  const handleProfessionalTouchEnd = () => {
    if (!professionalIsDragging) return;
    setProfessionalIsDragging(false);
    
    const movedBy = professionalCurrentTranslate - professionalPrevTranslate;
    const threshold = 100;
    
    if (movedBy < -threshold && professionalCurrentSlide < professionalSteps.length - 1) {
      nextProfessionalSlide();
    } else if (movedBy > threshold && professionalCurrentSlide > 0) {
      prevProfessionalSlide();
    }
  };

  // Update transforms when slides change
  useEffect(() => {
    const newTranslate = -patientCurrentSlide * 100;
    setPatientCurrentTranslate(newTranslate);
    setPatientPrevTranslate(newTranslate);
  }, [patientCurrentSlide]);

  useEffect(() => {
    const newTranslate = -professionalCurrentSlide * 100;
    setProfessionalCurrentTranslate(newTranslate);
    setProfessionalPrevTranslate(newTranslate);
  }, [professionalCurrentSlide]);

  const handleGetStarted = () => {
    navigate('/register');
  };

  return (
    <div className="hiw-page-wrapper">
      {/* Hero Section */}
      <section className="hiw-hero-section">
        <div className="hiw-hero-container">
          <div className="hiw-hero-content">
            <div className="hiw-hero-badge">
              <span className="hiw-badge-text">HOW IT WORKS</span>
            </div>
            
            <h1 className="hiw-hero-title">
              Simplifying Mental Health Care
              <br />
              <span className="hiw-hero-title-highlight">
                For Every Step of Your Journey
              </span>
            </h1>
            <p className="hiw-hero-subtitle">
              With Compassion, Understanding, and You at the Center
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="hiw-main-section">
        <div className="hiw-main-container">
          
          {/* FOR PATIENTS SECTION */}
          <div className="hiw-section-header">
            <div className="hiw-section-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <path d="M33.333 35v-3.333a6.667 6.667 0 0 0-6.666-6.667H13.333a6.667 6.667 0 0 0-6.666 6.667V35" stroke="#4CAF50" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="20" cy="11.667" r="6.667" stroke="#4CAF50" strokeWidth="3"/>
              </svg>
            </div>
            <div className="hiw-section-title-content">
              <h2 className="hiw-section-title">For Patients</h2>
            </div>
          </div>

          {/* Patient Carousel */}
          <div 
            className="hiw-carousel-track"
            ref={patientSliderRef}
            onMouseDown={handlePatientTouchStart}
            onMouseMove={handlePatientTouchMove}
            onMouseUp={handlePatientTouchEnd}
            onMouseLeave={handlePatientTouchEnd}
            onTouchStart={handlePatientTouchStart}
            onTouchMove={handlePatientTouchMove}
            onTouchEnd={handlePatientTouchEnd}
            style={{ 
              transform: `translateX(${patientCurrentTranslate}%)`,
              cursor: patientIsDragging ? 'grabbing' : 'grab'
            }}
          >
            {patientSteps.map((step, index) => (
              <div key={step.id} className="hiw-carousel-slide">
                <div className="hiw-step-card">
                  <div className="hiw-step-icon" style={{ backgroundColor: `${step.color}20` }}>
                    <img src={step.icon} alt={step.title} className="hiw-step-icon-img" />
                  </div>
                  <div className="hiw-step-content">
                    <h3 className="hiw-step-title">{step.title}</h3>
                    <p className="hiw-step-description">{step.subtitle}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Patient Carousel Navigation */}
          <div className="hiw-carousel-navigation">
            <button 
              className="hiw-carousel-btn hiw-carousel-prev" 
              onClick={prevPatientSlide}
              disabled={patientCurrentSlide === 0}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            <div className="hiw-carousel-indicators">
              {patientSteps.map((_, index) => (
                <button
                  key={index}
                  className={`hiw-carousel-indicator ${index === patientCurrentSlide ? 'hiw-indicator-active' : ''}`}
                  onClick={() => goToPatientSlide(index)}
                />
              ))}
            </div>
            
            <button 
              className="hiw-carousel-btn hiw-carousel-next" 
              onClick={nextPatientSlide}
              disabled={patientCurrentSlide === patientSteps.length - 1}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* FOR MENTAL HEALTH PROFESSIONALS SECTION */}
          <div className="hiw-section-header hiw-section-header-professional">
            <div className="hiw-section-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <path d="M15 20l3.333 3.333L25 16.667" stroke="#667eea" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M35 20c-2.5 0-5-1.667-5-5s2.5-5 5-5 5 1.667 5 5-2.5 5-5 5" stroke="#667eea" strokeWidth="3"/>
                <path d="M5 20c2.5 0 5-1.667 5-5s-2.5-5-5-5-5 1.667-5 5 2.5 5 5 5" stroke="#667eea" strokeWidth="3"/>
                <path d="M20 35c0-2.5-1.667-5-5-5s-5 2.5-5 5 1.667 5 5 5 5-2.5 5-5" stroke="#667eea" strokeWidth="3"/>
                <path d="M20 5c0 2.5-1.667 5-5 5s-5-2.5-5-5 1.667-5 5-5 5 2.5 5 5" stroke="#667eea" strokeWidth="3"/>
              </svg>
            </div>
            <div className="hiw-section-title-content">
              <h2 className="hiw-section-title">For Mental Health Professionals</h2>
            </div>
          </div>

          {/* Professional Carousel */}
          <div 
            className="hiw-carousel-track"
            ref={professionalSliderRef}
            onMouseDown={handleProfessionalTouchStart}
            onMouseMove={handleProfessionalTouchMove}
            onMouseUp={handleProfessionalTouchEnd}
            onMouseLeave={handleProfessionalTouchEnd}
            onTouchStart={handleProfessionalTouchStart}
            onTouchMove={handleProfessionalTouchMove}
            onTouchEnd={handleProfessionalTouchEnd}
            style={{ 
              transform: `translateX(${professionalCurrentTranslate}%)`,
              cursor: professionalIsDragging ? 'grabbing' : 'grab'
            }}
          >
            {professionalSteps.map((step, index) => (
              <div key={step.id} className="hiw-carousel-slide">
                <div className="hiw-step-card">
                  <div className="hiw-step-icon" style={{ backgroundColor: `${step.color}20` }}>
                    <img src={step.icon} alt={step.title} className="hiw-step-icon-img" />
                  </div>
                  <div className="hiw-step-content">
                    <h3 className="hiw-step-title">{step.title}</h3>
                    <p className="hiw-step-description">{step.subtitle}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Professional Carousel Navigation */}
          <div className="hiw-carousel-navigation">
            <button 
              className="hiw-carousel-btn hiw-carousel-prev" 
              onClick={prevProfessionalSlide}
              disabled={professionalCurrentSlide === 0}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            <div className="hiw-carousel-indicators">
              {professionalSteps.map((_, index) => (
                <button
                  key={index}
                  className={`hiw-carousel-indicator ${index === professionalCurrentSlide ? 'hiw-indicator-active' : ''}`}
                  onClick={() => goToProfessionalSlide(index)}
                />
              ))}
            </div>
            
            <button 
              className="hiw-carousel-btn hiw-carousel-next" 
              onClick={nextProfessionalSlide}
              disabled={professionalCurrentSlide === professionalSteps.length - 1}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;